import React, { useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Alert,
  Typography,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Rotate90DegreesCw as RotateIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useOcrDataProcessor } from '../hooks/useOcrDataProcessor';
import { ProcessingModal } from './ProcessingModal';
import Button from '../../../core-components/theme/components/Button/Button';

const getRotatedImageFile = (imageSrc: string, rotation: number): Promise<File> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.src = imageSrc;

    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        return reject(new Error('Could not get canvas context.'));
      }

      const isSwapped = rotation === 90 || rotation === 270;
      canvas.width = isSwapped ? image.height : image.width;
      canvas.height = isSwapped ? image.width : image.height;

      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(rotation * (Math.PI / 180));
      ctx.drawImage(image, -image.width / 2, -image.height / 2);

      canvas.toBlob(blob => {
        if (!blob) {
          return reject(new Error('Canvas toBlob operation failed.'));
        }
        const rotatedFile = new File([blob], 'rotated_image.png', { type: 'image/png' });
        resolve(rotatedFile);
      }, 'image/png');
    };

    image.onerror = error => reject(error);
  });
};

interface PreviewModalProps {
  isOpen: boolean;
  image: { file: File; dataUrl: string };
  templateId: string;
  assetId: string;
  logbookTemplateId: string;
  onClose: () => void;
}

export const PreviewModal: React.FC<PreviewModalProps> = ({
  isOpen,
  image,
  templateId,
  assetId,
  logbookTemplateId,
  onClose,
}) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [rotation, setRotation] = useState(0);

  const { processOcr, isProcessing, isError, processingError, resetMutation } = useOcrDataProcessor(
    image.dataUrl
  );

  const handleConfirm = async () => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    let fileToSend = image.file;

    if (rotation !== 0) {
      try {
        fileToSend = await getRotatedImageFile(image.dataUrl, rotation);
      } catch (error) {
        toast.error('Failed to apply image rotation. Please try again.');
        return;
      }
    }

    processOcr(
      {
        imageFile: fileToSend,
        templateId,
        assetId,
        logbookTemplateId,
        signal: controller.signal,
      },
      {
        onSuccess: () => {
          handleClose();
        },
      }
    );
  };

  const handleClose = () => {
    if (isProcessing && abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    onClose();
  };

  const handleRetry = () => {
    resetMutation();
    handleConfirm();
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose} maxWidth="md">
        <DialogTitle>Preview and Correct Orientation</DialogTitle>
        <DialogContent>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography fontWeight="bold">Error Detected</Typography>
              {processingError?.message || 'An error occurred.'}
            </Alert>
          )}
          <Box sx={{ textAlign: 'center' }}>
            <img
              src={image.dataUrl}
              alt="Preview"
              style={{
                maxWidth: '100%',
                maxHeight: '60vh',
                transition: 'transform 0.3s ease-in-out',
                transform: `rotate(${rotation}deg)`,
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Tooltip title="Rotate 90°">
            <IconButton
              onClick={() => setRotation(prev => (prev + 90) % 360)}
              disabled={isProcessing}
            >
              <RotateIcon />
            </IconButton>
          </Tooltip>

          <Box>
            <Button onClick={handleClose} variant="outlined" disabled={isProcessing}>
              Cancel
            </Button>
            {isError ? (
              <Button
                onClick={handleRetry}
                variant="contained"
                color="warning"
                disabled={isProcessing}
                sx={{ ml: 1 }}
              >
                Retry
              </Button>
            ) : (
              <Button
                onClick={handleConfirm}
                variant="contained"
                disabled={isProcessing}
                sx={{ ml: 1 }}
              >
                Confirm
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>
      <ProcessingModal isOpen={isProcessing} onClose={handleClose} />
    </>
  );
};
